import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Popconfirm,
  Progress,
  Result,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  RedoOutlined,
  ReloadOutlined,
  SwapOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { vendorApi, TransferNotFoundError, TransferRetryError } from '@/utils/vendorApi';
import { useAuthStore } from '@/store/authStore';
import { canReadTechnicalInBu, canRetryTransfer, canSubmitAdHoc } from '@/store/transferAccess';
import { submittedByLabel, transferOutcomeDisplay, transferStatusDisplay, triggeredByLabel } from '@/components/transfer/transferStatus';
import { theme } from '@/theme';
import { TransferHistoryEntry, TransferItem } from '@/types';

const { Text, Paragraph } = Typography;

// Brand palette.
const BRAND = {
  yellow: '#FFC700',
  cyan: '#01C1E3',
  navy: '#06063D',
};

function knownMaxAttempts(transfer: TransferItem): number | undefined {
  if (typeof transfer.maxAttempts === 'number') {
    return transfer.maxAttempts;
  }
  const fromMetadata = transfer.metadata?.max_attempts;
  return typeof fromMetadata === 'number' ? fromMetadata : undefined;
}

// A failed row is still in the pipeline when another attempt is scheduled.
function isStillRetrying(transfer: TransferItem): boolean {
  if (transfer.status !== 'failed') {
    return false;
  }
  const maxAttempts = knownMaxAttempts(transfer);
  if (typeof transfer.attempt !== 'number' || typeof maxAttempts !== 'number') {
    return false;
  }
  return transfer.attempt + 1 < maxAttempts;
}

function retryEndedInFailure(transfer: TransferItem): boolean {
  if (transfer.status === 'retries_exhausted') {
    return true;
  }
  return transfer.status === 'failed' && !isStillRetrying(transfer);
}

function shouldKeepRefreshing(transfer: TransferItem): boolean {
  return isLiveStatus(transfer.status) || isStillRetrying(transfer);
}

function failureSummary(transfer: TransferItem, canRetry: boolean): string | null {
  if (transfer.status === 'retries_exhausted') {
    return canRetry
      ? 'Automatic retries are exhausted. Use Retry transfer to start a new retry cycle.'
      : 'Automatic retries are exhausted.';
  }
  if (isStillRetrying(transfer)) {
    return 'This attempt failed. Another attempt is scheduled.';
  }
  if (transfer.status === 'failed') {
    return typeof knownMaxAttempts(transfer) === 'number'
      ? 'Automatic retries are exhausted.'
      : 'This attempt failed.';
  }
  return null;
}

// Maps a few high-confidence error patterns to guidance. Everything else
// falls back to showing the raw error only.
function errorHint(errorType?: string | null, errorMessage?: string | null): string | null {
  const text = `${errorType || ''} ${errorMessage || ''}`.toLowerCase();
  if (text.includes('nonetype') && (text.includes('pathlike') || text.includes('str, bytes'))) {
    return 'A required file path was missing. Check the vendor configuration for a missing source folder or destination.';
  }
  return null;
}

function isEpoch(value: string | number): boolean {
  return /^\d+$/.test(String(value).trim());
}

function stampTime(value?: string | number | null): number {
  if (value == null || value === '') {
    return 0;
  }
  const parsed = isEpoch(value) ? dayjs.unix(Number(value)) : dayjs(value);
  const millis = parsed.valueOf();
  return Number.isFinite(millis) ? millis : 0;
}

function formatTimestamp(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const parsed = isEpoch(value) ? dayjs.unix(Number(value)) : dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm:ss') : '—';
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;
const AUTO_REFRESH_MS = 5000;

function retryFailureMessage(error: TransferRetryError): string {
  switch (error.errorCode) {
    case 'NOT_RETRYABLE':
      return 'This transfer is no longer eligible for retry.';
    case 'DIRECTION_NOT_RETRYABLE':
      return "Inbound transfers can't be retried from the Console.";
    case 'VENDOR_ARCHIVED':
      return "This vendor is archived, so the transfer can't be retried.";
    case 'VENDOR_INACTIVE':
      return "This vendor is currently inactive, so the transfer can't be retried.";
    case 'VENDOR_NOT_FOUND':
      return "The vendor for this transfer couldn't be found.";
    case 'RETRY_IN_PROGRESS':
      return 'A retry for this transfer is already in progress.';
    case 'ENDPOINT_UNKNOWN':
      return "This transfer's file share could not be determined. Contact CEDI support.";
    case 'FORBIDDEN_ROLE':
      return "You don't have permission to retry transfers.";
    case 'UNKNOWN_TRANSFER':
      return 'Transfer not found.';
    case 'LOOKUP_FAILED':
    case 'START_FAILED':
      return "The retry couldn't be started. Please try again or contact CEDI support.";
    default:
      return (error.errorCode && error.message)
        ? error.message
        : "The retry couldn't be started. Please try again or contact CEDI support.";
  }
}

function stillAwaitingRetry(status?: string): boolean {
  return status === 'failed' || status === 'retries_exhausted';
}

function isLiveStatus(status?: string | null): boolean {
  return status === 'pending' || status === 'in_flight' || status === 'retry_scheduled';
}

function isTechnicalMetadataKey(key: string): boolean {
  return (
    key.endsWith('_arn')
    || key.endsWith('_execution_name')
    || key === 'execution_name'
    || key === 'state_machine_name'
    || key === 'retry_execution_name'
  );
}

function isSecretKey(key: string): boolean {
  return key.toLowerCase().includes('secret');
}

function withoutSecretKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => withoutSecretKeys(entry));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !isSecretKey(key))
        .map(([key, entry]) => [key, withoutSecretKeys(entry)]),
    );
  }
  return value;
}

function shownHistoryDestination(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(withoutSecretKeys(value));
  }
  return '';
}

function historyAttemptLabel(entry: TransferHistoryEntry): string | null {
  if (typeof entry.attempt !== 'number' || typeof entry.maxAttempts !== 'number') {
    return null;
  }
  return `Attempt ${entry.attempt + 1} of ${entry.maxAttempts}`;
}

function historyWhoLine(entry: TransferHistoryEntry): string | null {
  if (entry.outcome === 'retry_requested' || entry.outcome === 'resubmit_requested') {
    const email = submittedByLabel(entry.triggeredBy);
    return email === '—' ? null : `by ${email}`;
  }
  return triggeredByLabel(entry.triggeredBy);
}

function displayedTimestamp(value?: string | number | null): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = isEpoch(value) ? dayjs.unix(Number(value)) : dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm:ss') : null;
}

function formatRunTime(started?: string | null, ended?: string | null): string | null {
  const start = stampTime(started);
  const end = stampTime(ended);
  if (!start || !end || end < start) {
    return null;
  }
  const total = Math.round((end - start) / 1000);
  if (total < 60) {
    return `${total} s`;
  }
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes < 60) {
    return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;
  if (remainMinutes === 0 && seconds === 0) {
    return `${hours} h`;
  }
  if (seconds === 0) {
    return `${hours} h ${remainMinutes} min`;
  }
  return `${hours} h ${remainMinutes} min ${seconds} s`;
}

function historySummary(transfer: TransferItem): { label: string; value: string }[] {
  const facts: { label: string; value: string }[] = [];
  const started = displayedTimestamp(transfer.startedAt);
  if (started) {
    facts.push({ label: 'Latest run started', value: started });
  }
  if (isLiveStatus(transfer.status) || isStillRetrying(transfer)) {
    facts.push({
      label: 'In progress',
      value: isStillRetrying(transfer)
        ? 'Retrying'
        : transferStatusDisplay(transfer.status).label,
    });
    const updated = displayedTimestamp(transfer.lastTransitionAt);
    if (updated) {
      facts.push({ label: 'Last updated', value: updated });
    }
    return facts;
  }
  const endedSource = displayedTimestamp(transfer.completedAt)
    ? transfer.completedAt
    : displayedTimestamp(transfer.lastTransitionAt)
      ? transfer.lastTransitionAt
      : null;
  const ended = endedSource ? displayedTimestamp(endedSource) : null;
  if (ended) {
    facts.push({ label: 'Ended', value: ended });
  }
  const runtime = formatRunTime(transfer.startedAt, endedSource);
  if (runtime) {
    facts.push({ label: 'Run time', value: runtime });
  }
  return facts;
}

function outcomeIcon(outcome: string | null | undefined, color: string) {
  const style = { color, fontSize: 16 };
  if (outcome === 'completed') {
    return <CheckCircleOutlined style={style} />;
  }
  if (outcome === 'rejected' || outcome === 'start_failed' || outcome === 'failed_final') {
    return <CloseCircleOutlined style={style} />;
  }
  if (outcome === 'resubmitted') {
    return <SwapOutlined style={style} />;
  }
  if (outcome === 'retry_requested' || outcome === 'resubmit_requested') {
    return <RedoOutlined style={style} />;
  }
  return <InfoCircleOutlined style={style} />;
}

function historyDetailLine(entry: TransferHistoryEntry): string | null {
  const attemptLabel = historyAttemptLabel(entry);
  const total = typeof entry.totalAttempts === 'number' ? `${entry.totalAttempts} total` : null;
  const parts = [attemptLabel, total].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : null;
}

const HISTORY_PREVIEW = 5;

function totalAttemptsTooltip(maxAttempts?: number): string {
  const lead = 'Total attempts across every run of this transfer, including manual retries.';
  if (typeof maxAttempts !== 'number') {
    return lead;
  }
  return `${lead} Each run makes up to ${maxAttempts} automatic attempts.`;
}

function transitionKey(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
}

// Ad hoc gets its own accent (Pollen Yellow) so it stands out from scheduled.
function triggeredByColor(triggeredBy?: string | null): string {
  return (triggeredBy || '').startsWith('api:ad_hoc:') ? BRAND.yellow : BRAND.navy;
}

// Metadata keys already shown elsewhere. Skipped so the panel doesn't repeat them.
const METADATA_SKIP = new Set([
  'bu', 'vendor_id', 'direction', 'source', 'destination', 'triggered_by',
  'attempt', 'attempts_used', 'max_attempts', 'total_attempts', 'start_timestamp', 'completed_at',
  'complheted_at', 'end_timestamp', 'expires_at',
]);

const METADATA_LABELS: Record<string, string> = {
  duration_seconds: 'Duration',
  attempts_used: 'Attempts used',
  max_attempts: 'Max attempts',
  retry_execution_name: 'Retry execution',
  retry_execution_arn: 'Retry execution ARN',
  target_lambda_arn: 'Target lambda',
};

function humanizeKey(key: string): string {
  return METADATA_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(seconds: number): string {
  if (seconds < 1) {
    return `${Math.round(seconds * 1000)} ms`;
  }
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`;
  }
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatMetadataValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (key === 'duration_seconds' && typeof value === 'number') {
    return formatDuration(value);
  }
  if (typeof value === 'number') {
    return value > 1_000_000_000 ? formatTimestamp(value) : String(value);
  }
  if (typeof value === 'string') {
    return /\d{4}-\d{2}-\d{2}T/.test(value) && dayjs(value).isValid() ? formatTimestamp(value) : value;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return JSON.stringify(value);
}

function displayMetadataValue(key: string, value: unknown): string {
  const formatted = formatMetadataValue(key, value);
  return formatted.includes('@') ? formatted.toLowerCase() : formatted;
}

function lowercaseMetadataEmails(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.includes('@') ? value.toLowerCase() : value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => lowercaseMetadataEmails(entry));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        lowercaseMetadataEmails(entry),
      ]),
    );
  }
  return value;
}

const TransferDetailPage: React.FC = () => {
  const { message } = App.useApp();
  const { token } = antdTheme.useToken();
  const { authorization } = useAuthStore();
  const navigate = useNavigate();
  const params = useParams();
  const transferId = params['*'] ? decodeURIComponent(params['*']) : '';
  const cancelledRef = useRef(false);
  const loadInFlightRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryingRef = useRef(false);

  const [transfer, setTransfer] = useState<TransferItem | null>(null);
  const [history, setHistory] = useState<TransferHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [pollFailedAgain, setPollFailedAgain] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const autoRefreshRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const loadTransfer = useCallback(async (quiet = false): Promise<TransferItem | null> => {
    if (!transferId) {
      if (!quiet) {
        setNotFound(true);
        setLoading(false);
      }
      return null;
    }
    if (quiet && loadInFlightRef.current) {
      return null;
    }
    loadInFlightRef.current = true;
    if (!quiet) {
      setLoading(true);
      setTransfer(null);
      setHistory([]);
      setNotFound(false);
      setLoadError(false);
    }
    try {
      const loaded = await vendorApi.getTransfer(transferId);
      const result = loaded.transfer;
      if (!cancelledRef.current) {
        setTransfer(result);
        setHistory(loaded.history);
        if (!stillAwaitingRetry(result.status)) {
          setPollTimedOut(false);
          setPollFailedAgain(false);
        } else if (!retryEndedInFailure(result)) {
          setPollFailedAgain(false);
        }
      }
      return cancelledRef.current ? null : result;
    } catch (error: unknown) {
      if (cancelledRef.current || quiet) {
        return null;
      }
      if (error instanceof TransferNotFoundError) {
        setNotFound(true);
      } else {
        setLoadError(true);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load transfer';
        message.error(errorMessage);
      }
      return null;
    } finally {
      loadInFlightRef.current = false;
      if (!cancelledRef.current && !quiet) {
        setLoading(false);
      }
    }
  }, [transferId, message]);

  const startPolling = useCallback((baseline?: string | number | null) => {
    stopPolling();
    autoRefreshRef.current = false;
    setAutoRefreshing(false);
    setPolling(true);
    setPollTimedOut(false);
    setPollFailedAgain(false);
    const startedAt = Date.now();
    const baselineKey = transitionKey(baseline);
    pollTimerRef.current = setInterval(() => {
      if (cancelledRef.current) {
        return;
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        stopPolling();
        setPolling(false);
        setPollTimedOut(true);
        return;
      }
      if (loadInFlightRef.current) {
        return;
      }
      void loadTransfer(true).then((result) => {
        if (cancelledRef.current || !result) {
          return;
        }
        if (!stillAwaitingRetry(result.status)) {
          stopPolling();
          setPolling(false);
          setPollTimedOut(false);
          setPollFailedAgain(false);
          return;
        }
        if (transitionKey(result.lastTransitionAt) !== baselineKey) {
          stopPolling();
          setPolling(false);
          setPollTimedOut(false);
          setPollFailedAgain(retryEndedInFailure(result));
        }
      });
    }, POLL_INTERVAL_MS);
  }, [loadTransfer, stopPolling]);

  const startAutoRefresh = useCallback(() => {
    if (autoRefreshRef.current && pollTimerRef.current != null) {
      return;
    }
    stopPolling();
    autoRefreshRef.current = true;
    setAutoRefreshing(true);
    pollTimerRef.current = setInterval(() => {
      if (cancelledRef.current || loadInFlightRef.current) {
        return;
      }
      void loadTransfer(true).then((result) => {
        if (cancelledRef.current || !result) {
          return;
        }
        if (!shouldKeepRefreshing(result)) {
          autoRefreshRef.current = false;
          setAutoRefreshing(false);
          stopPolling();
        }
      });
    }, AUTO_REFRESH_MS);
  }, [loadTransfer, stopPolling]);

  useEffect(() => {
    setHistoryExpanded(false);
  }, [transferId]);

  useEffect(() => {
    cancelledRef.current = false;
    void loadTransfer();
    return () => {
      cancelledRef.current = true;
      autoRefreshRef.current = false;
      stopPolling();
    };
  }, [loadTransfer, stopPolling]);

  useEffect(() => {
    if (polling) {
      return;
    }
    if (transfer && shouldKeepRefreshing(transfer)) {
      startAutoRefresh();
      return;
    }
    if (autoRefreshRef.current) {
      autoRefreshRef.current = false;
      setAutoRefreshing(false);
      stopPolling();
    }
  }, [transfer, polling, startAutoRefresh, stopPolling]);

  const handleRetry = async () => {
    if (retryingRef.current || !transferId) {
      return;
    }
    retryingRef.current = true;
    setRetrying(true);
    try {
      await vendorApi.retryTransfer(transferId);
      message.success('Retry started.');
      startPolling(transfer?.lastTransitionAt);
    } catch (error: unknown) {
      if (error instanceof TransferRetryError) {
        message.error(retryFailureMessage(error));
        if (error.errorCode === 'NOT_RETRYABLE') {
          void loadTransfer();
        } else if (error.errorCode === 'RETRY_IN_PROGRESS') {
          startPolling(transfer?.lastTransitionAt);
        }
        return;
      }
      message.error("The retry couldn't be started. Please try again or contact CEDI support.");
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };

  const copy = (label: string, value?: string | null) => {
    if (!value) {
      return;
    }
    void navigator.clipboard.writeText(value);
    message.success(`${label} copied`);
  };

  // Small inline copy button for a row value.
  const copyIcon = (label: string, value?: string | null) =>
    value ? (
      <Tooltip title={`Copy ${label.toLowerCase()}`}>
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined style={{ color: BRAND.cyan }} />}
          onClick={() => copy(label, value)}
        />
      </Tooltip>
    ) : null;

  const backButton = (
    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/transfers')}>
      Back to Transfers
    </Button>
  );

  const refreshButton = (
    <Button icon={<ReloadOutlined />} onClick={() => void loadTransfer()}>
      Refresh
    </Button>
  );

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        {backButton}
        <div style={{ marginTop: 64, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ padding: 24 }}>
        <Result status="404" title="Transfer not found" subTitle={transferId} extra={backButton} />
      </div>
    );
  }

  if (loadError || !transfer) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="error"
          title="Unable to load transfer"
          extra={
            <Space>
              {backButton}
              <Button type="primary" icon={<ReloadOutlined />} onClick={() => void loadTransfer()}>
                Reload
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  const meta = transferStatusDisplay(transfer.status);
  const isAttemptRecord = transfer.status === 'rejected' || transfer.status === 'resubmitted';
  const canReadTechnical = canReadTechnicalInBu(authorization, transfer.bu);
  const canRetry = canRetryTransfer(authorization, transfer.bu);
  const hasError = !!(transfer.errorType || transfer.errorMessage);
  const summary = failureSummary(transfer, canRetry && transfer.retryable === true);
  const hint = errorHint(transfer.errorType, transfer.errorMessage);

  // Source falls back to the transfer path (transfer_id / correlation_id) when
  // the record didn't record a separate source.
  const sourceValue = transfer.source || transfer.transferId;

  // Reconcile the two attempt numbers. The retry orchestrator's one-based
  // attempts_used/max_attempts win when present; otherwise the record's
  // zero-based attempt is shown one-based so it reads naturally.
  const md = (transfer.metadata || {}) as Record<string, unknown>;
  const attemptsUsed = typeof md.attempts_used === 'number' ? md.attempts_used : undefined;
  const maxAttempts = knownMaxAttempts(transfer);
  const attemptNumber = attemptsUsed !== undefined
    ? attemptsUsed
    : typeof transfer.attempt === 'number'
      ? transfer.attempt + 1
      : undefined;
  const attemptsLabel = attemptNumber === undefined
    ? '—'
    : typeof maxAttempts === 'number'
      ? `${attemptNumber} of ${maxAttempts}`
      : `Attempt ${attemptNumber}`;
  const hasTotalAttempts = typeof transfer.totalAttempts === 'number';
  const attemptsShown = hasTotalAttempts
    ? shouldKeepRefreshing(transfer)
      ? `${transfer.totalAttempts} so far`
      : String(transfer.totalAttempts)
    : attemptsLabel;
  const attemptsHint = totalAttemptsTooltip(maxAttempts);
  const safeMetadata = withoutSecretKeys(transfer.metadata);
  const hasMetadata = !!safeMetadata
    && typeof safeMetadata === 'object'
    && !Array.isArray(safeMetadata)
    && Object.keys(safeMetadata as Record<string, unknown>).length > 0;
  const metadataEntries = hasMetadata
    ? Object.entries(safeMetadata as Record<string, unknown>).filter(
      ([key]) => !METADATA_SKIP.has(key) && !isSecretKey(key),
    )
    : [];
  const publicMetadataRows = metadataEntries.filter(([key]) => !isTechnicalMetadataKey(key));
  const technicalMetadataRows = metadataEntries.filter(([key]) => isTechnicalMetadataKey(key));
  const latestExecutionArn = history.find((entry) => entry.executionArn)?.executionArn;
  const orderedHistory = [...history].sort((left, right) => stampTime(right.at) - stampTime(left.at));
  const visibleHistory = historyExpanded ? orderedHistory : orderedHistory.slice(0, HISTORY_PREVIEW);
  const hiddenHistoryCount = Math.max(orderedHistory.length - HISTORY_PREVIEW, 0);
  const summaryFacts = historySummary(transfer);

  const showRetry = canRetry && transfer.retryable === true && !polling;
  const showResubmit = transfer.status === 'rejected' && canSubmitAdHoc(authorization, transfer.bu);
  const exhaustedNote = transfer.status === 'retries_exhausted'
    ? maxAttempts !== undefined
      ? `Automatic retries are exhausted. This starts a new retry cycle (up to ${maxAttempts} attempts).`
      : 'Automatic retries are exhausted. This starts a new retry cycle.'
    : null;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        {backButton}
        <Space>
          {showRetry ? (
            <Popconfirm
              title="Retry this transfer?"
              description={
                <div style={{ maxWidth: 360 }}>
                  <div>Vendor: {transfer.vendorId || '—'}</div>
                  <div>Status: {meta.label}</div>
                  <div>Attempts: {attemptsLabel}</div>
                  <div style={{ wordBreak: 'break-all' }}>Source: {sourceValue || '—'}</div>
                  {exhaustedNote ? <div style={{ marginTop: 8 }}>{exhaustedNote}</div> : null}
                  <div style={{ marginTop: 8 }}>
                    The original file must still be in its source location for the retry to succeed.
                  </div>
                </div>
              }
              okText="Retry"
              cancelText="Cancel"
              onConfirm={() => { void handleRetry(); }}
            >
              <Button type="primary" loading={retrying} disabled={retrying}>
                Retry transfer
              </Button>
            </Popconfirm>
          ) : null}
          {showResubmit ? (
            <Button
              type="primary"
              onClick={() => navigate(
                `/transfers/ad-hoc?resubmitOf=${encodeURIComponent(transfer.transferId)}`
              )}
            >
              Fix and resubmit
            </Button>
          ) : null}
          {autoRefreshing ? (
            <Text type="secondary" style={{ fontSize: 12 }}>Updating automatically</Text>
          ) : null}
          {refreshButton}
        </Space>
      </div>

      {polling ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Retry submitted. Waiting for the transfer to update…"
        />
      ) : null}
      {pollTimedOut && !polling ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="The retry was submitted but the status hasn't updated yet. Use Refresh to check again."
        />
      ) : null}
      {pollFailedAgain && !polling ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          message="The retry ran but the transfer failed again. See the error details below."
        />
      ) : null}
      {transfer.status === 'rejected' ? (
        <Alert
          style={{ marginBottom: 16, fontFamily: theme.token?.fontFamily }}
          type="error"
          showIcon
          message="Ad hoc request rejected"
          description={
            <>
              <div>{transfer.errorMessage || '—'}</div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Path: </Text>
                <Text style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {transfer.source || '—'}
                </Text>
              </div>
              <div>
                <Text type="secondary">Submitted by: </Text>
                {submittedByLabel(transfer.triggeredBy)}
              </div>
              <div>
                <Text type="secondary">When: </Text>
                {formatTimestamp(transfer.startedAt || transfer.lastTransitionAt)}
              </div>
            </>
          }
        />
      ) : null}
      {transfer.status === 'resubmitted' ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Resubmitted as"
          description={
            <>
              {transfer.resubmittedAs ? (
                <Link to={`/transfers/detail/${encodeURIComponent(transfer.resubmittedAs)}`}>
                  {transfer.resubmittedAs}
                </Link>
              ) : (
                '—'
              )}
              {transfer.resubmittedBy ? <div>By {transfer.resubmittedBy.toLowerCase()}</div> : null}
              {transfer.errorMessage ? (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{transfer.errorMessage}</Text>
                </div>
              ) : null}
            </>
          }
        />
      ) : null}

      {/* Hero */}
      <Card style={{ marginBottom: 16, borderTop: `3px solid ${meta.color}` }} styles={{ body: { padding: '20px 24px' } }}>
        <Space align="start" size="large" style={{ width: '100%' }}>
          <span style={{ fontSize: 44, color: meta.color, lineHeight: 1 }}>{meta.icon}</span>
          <div style={{ flex: 1 }}>
            <Space size="middle" align="center" wrap>
              <Tag color={meta.color} style={{ fontSize: 14, padding: '2px 12px', margin: 0 }}>
                {meta.label}
              </Tag>
              <Tag
                color={triggeredByColor(transfer.triggeredBy)}
                style={triggeredByColor(transfer.triggeredBy) === BRAND.yellow ? { color: BRAND.navy } : undefined}
              >
                {triggeredByLabel(transfer.triggeredBy)}
              </Tag>
              {transfer.direction && <Text type="secondary">{transfer.direction}</Text>}
            </Space>
            <Paragraph
              copyable={{ text: transfer.transferId }}
              style={{ margin: '10px 0 0', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}
            >
              {transfer.transferId}
            </Paragraph>
          </div>
        </Space>
        <Progress
          percent={meta.progress}
          strokeColor={meta.color}
          showInfo={false}
          status={transfer.status === 'in_flight' ? 'active' : 'normal'}
          style={{ marginTop: 12, marginBottom: 0 }}
        />
      </Card>

      {/* Two columns: Details (with inline copy) + History */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Card title="Details" style={{ flex: '1 1 480px' }}>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Vendor">{transfer.vendorId || '—'}</Descriptions.Item>
            <Descriptions.Item label="Business unit">{transfer.bu || '—'}</Descriptions.Item>
            <Descriptions.Item label="Direction">{transfer.direction || '—'}</Descriptions.Item>
            {!isAttemptRecord ? (
              <Descriptions.Item label="Attempts">
                <Space size={6}>
                  <span>{attemptsShown}</span>
                  {hasTotalAttempts ? (
                    <Tooltip title={attemptsHint}>
                      <InfoCircleOutlined
                        aria-label={attemptsHint}
                        style={{ color: token.colorTextSecondary, cursor: 'help' }}
                      />
                    </Tooltip>
                  ) : null}
                </Space>
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Source">
              <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {sourceValue || '—'}
                </Text>
                {copyIcon('Source path', sourceValue)}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Destination">
              <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                {transfer.destination ? (
                  <Text style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                    {transfer.destination}
                  </Text>
                ) : (
                  <Text type="secondary">Not recorded</Text>
                )}
                {copyIcon('Destination path', transfer.destination)}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Transfer ID">
              <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {transfer.transferId}
                </Text>
                {copyIcon('Transfer ID', transfer.transferId)}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          title={(
            <span style={{ fontFamily: token.fontFamily }}>
              History{' '}
              <Text type="secondary" style={{ fontWeight: 400, fontSize: 14 }}>
                ({orderedHistory.length} {orderedHistory.length === 1 ? 'event' : 'events'})
              </Text>
            </span>
          )}
          style={{ flex: '1 1 320px', minWidth: 0, maxWidth: '100%' }}
          styles={{ body: { overflow: 'hidden' } }}
        >
          {summaryFacts.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px 24px',
                fontFamily: token.fontFamily,
                marginBottom: 16,
              }}
            >
              {summaryFacts.map((fact) => (
                <div key={fact.label} style={{ minWidth: 0 }}>
                  <div style={{ color: token.colorTextSecondary, fontSize: 12 }}>{fact.label}</div>
                  <div style={{ fontWeight: 400 }}>{fact.value}</div>
                </div>
              ))}
            </div>
          ) : null}
          {visibleHistory.length > 0 ? (
            <div>
              <div style={{ fontFamily: token.fontFamily, fontWeight: 600, marginBottom: 8 }}>
                Audit trail
              </div>
              {visibleHistory.map((entry, index) => {
                const outcome = transferOutcomeDisplay(entry.outcome);
                const who = historyWhoLine(entry);
                const detail = historyDetailLine(entry);
                const when = displayedTimestamp(entry.at);
                const pathStyle = {
                  fontFamily: 'monospace',
                  wordBreak: 'break-all' as const,
                };
                return (
                  <div
                    key={`${entry.at || 'event'}-${entry.outcome || 'outcome'}-${index}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '12px 0',
                      borderTop: index === 0 ? `1px solid ${token.colorBorderSecondary}` : undefined,
                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                      {outcomeIcon(entry.outcome, outcome.color)}
                      {index < visibleHistory.length - 1 ? (
                        <div
                          style={{
                            flex: 1,
                            width: 1,
                            marginTop: 4,
                            background: token.colorBorderSecondary,
                          }}
                        />
                      ) : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Text strong>{outcome.label}</Text>
                        <Text type="secondary">
                          {index === 0 ? 'Latest' : null}
                          {index === 0 && when ? ' · ' : null}
                          {when}
                        </Text>
                      </div>
                      {who ? (
                        <div style={{ color: token.colorTextSecondary, marginTop: 2 }}>{who}</div>
                      ) : null}
                      {detail ? (
                        <div style={{ color: token.colorTextSecondary, marginTop: 2 }}>{detail}</div>
                      ) : null}
                      {(
                        entry.outcome === 'failed_final'
                        || entry.outcome === 'start_failed'
                        || entry.outcome === 'rejected'
                      ) && entry.errorType?.trim() ? (
                        <div style={{ color: token.colorTextSecondary, marginTop: 2 }}>
                          Error type: {entry.errorType.trim()}
                        </div>
                      ) : null}
                      {entry.message?.trim() ? (
                        <div style={{ marginTop: 4 }}>{entry.message}</div>
                      ) : null}
                      {entry.outcome === 'completed' && shownHistoryDestination(entry.destination) ? (
                        <div style={{ ...pathStyle, marginTop: 4 }}>{shownHistoryDestination(entry.destination)}</div>
                      ) : null}
                      {entry.outcome === 'resubmitted' && entry.resubmittedAs ? (
                        <div style={{ marginTop: 4, minWidth: 0 }}>
                          <Text type="secondary">Resubmitted as </Text>
                          <Link
                            to={`/transfers/detail/${encodeURIComponent(entry.resubmittedAs)}`}
                            style={pathStyle}
                          >
                            {entry.resubmittedAs}
                          </Link>
                        </div>
                      ) : null}
                      {entry.resubmittedFrom ? (
                        <div style={{ marginTop: 4, minWidth: 0 }}>
                          <Text type="secondary">Resubmitted from </Text>
                          <Link
                            to={`/transfers/detail/${encodeURIComponent(entry.resubmittedFrom)}`}
                            style={pathStyle}
                          >
                            {entry.resubmittedFrom}
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {hiddenHistoryCount > 0 ? (
                <Button
                  type="link"
                  onClick={() => setHistoryExpanded((open) => !open)}
                  style={{ paddingLeft: 0, marginTop: 4 }}
                >
                  {historyExpanded ? 'Show fewer' : `Show ${hiddenHistoryCount} earlier events`}
                </Button>
              ) : null}
            </div>
          ) : (
            <Text type="secondary" style={{ display: 'block' }}>
              No history recorded yet. History is recorded for activity after this update.
            </Text>
          )}
        </Card>
      </div>

      {!isAttemptRecord && (meta.failed || hasError) && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message={summary || 'This transfer did not complete successfully.'}
          description={
            <>
              {hint && (
                <Paragraph style={{ marginTop: 4, marginBottom: 8 }}>
                  <WarningOutlined style={{ color: BRAND.yellow, marginRight: 8 }} />
                  <Text strong>{hint}</Text>
                </Paragraph>
              )}
              {transfer.errorType && (
                <Paragraph style={{ marginBottom: 4 }}>
                  <Text type="secondary">Error type: </Text>
                  <Text code>{transfer.errorType}</Text>
                </Paragraph>
              )}
              {transfer.errorMessage && (
                <Paragraph style={{ marginBottom: 0 }}>
                  <Text type="secondary">Message: </Text>
                  {transfer.errorMessage}
                </Paragraph>
              )}
            </>
          }
        />
      )}

      {publicMetadataRows.length > 0 && (
        <Card title="Additional details" style={{ marginTop: 16 }}>
          <Descriptions bordered size="small" column={1}>
            {publicMetadataRows.map(([key, value]) => (
              <Descriptions.Item key={key} label={humanizeKey(key)}>
                <Text
                  style={
                    key.endsWith('_name')
                      ? { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }
                      : undefined
                  }
                >
                  {formatMetadataValue(key, value)}
                </Text>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      )}

      {canReadTechnical && (technicalMetadataRows.length > 0 || !!latestExecutionArn) && (
        <Collapse
          style={{ marginTop: 16 }}
          items={[
            {
              key: 'technical',
              label: 'Technical details',
              children: (
                <>
                  {(technicalMetadataRows.length > 0 || latestExecutionArn) && (
                    <Descriptions bordered size="small" column={1}>
                      {technicalMetadataRows.map(([key, value]) => (
                        <Descriptions.Item key={key} label={humanizeKey(key)}>
                          <Text style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                            {displayMetadataValue(key, value)}
                          </Text>
                        </Descriptions.Item>
                      ))}
                      {latestExecutionArn ? (
                        <Descriptions.Item label="Execution ARN">
                          <Text style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                            {latestExecutionArn}
                          </Text>
                        </Descriptions.Item>
                      ) : null}
                    </Descriptions>
                  )}
                  {hasMetadata ? (
                    <Collapse
                      ghost
                      style={{ marginTop: 8 }}
                      items={[
                        {
                          key: 'raw',
                          label: 'View raw metadata',
                          children: (
                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                              {JSON.stringify(lowercaseMetadataEmails(safeMetadata), null, 2)}
                            </pre>
                          ),
                        },
                      ]}
                    />
                  ) : null}
                </>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default TransferDetailPage;
